import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import { AlertCircle, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const processAuth = async (retryCount = 0) => {
    try {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];

      // Clear any stale session data before new login
      sessionStorage.clear();
      
      // First, try to logout any existing session to clear server-side state
      try {
        await axios.post(`${API}/auth/logout`, {}, { 
          withCredentials: true,
          timeout: 5000 
        });
      } catch (e) {
        // Ignore logout errors - we're just clearing stale state
      }

      // Exchange session_id for session_token with retry logic for mobile
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { 
          withCredentials: true,
          timeout: 15000 // Longer timeout for mobile
        }
      );

      // Store session token in localStorage as fallback for browsers that block cookies
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }

      // Remove session_token from user data before storing in state
      const { session_token, ...userData } = response.data;
      setUser(userData);
      
      // Get the intended redirect path (for mobile browsers)
      const redirectPath = sessionStorage.getItem('authRedirectPath') || '/dashboard';
      sessionStorage.removeItem('authRedirectPath');
      
      // Check if there's a pending agent application
      const pendingApp = localStorage.getItem('pendingAgentApplication');
      if (pendingApp) {
        navigate("/agents", { 
          replace: true,
          state: { user: userData, submitApplication: true }
        });
      } else {
        navigate(redirectPath, { 
          replace: true,
          state: { user: userData }
        });
      }
    } catch (error) {
      console.error("Auth callback error:", error);
      
      // Retry logic for network issues (including 503)
      if (retryCount < 2 && (!error.response || error.response?.status === 503)) {
        setRetrying(true);
        setTimeout(() => processAuth(retryCount + 1), 2000);
        return;
      }
      
      // Get the error message from the backend
      const errorMessage = error.response?.data?.detail || "";
      
      if (error.response?.status === 403) {
        setError(errorMessage || "Access denied. Your email is not authorized.");
      } else if (error.response?.status === 401) {
        setError(errorMessage || "Your sign-in link has expired. Please go back and click 'Sign In' again.");
      } else if (error.response?.status === 503) {
        setError(errorMessage || "Authentication service is temporarily unavailable. Please try again in a moment.");
      } else if (!error.response) {
        setError("Network error. Please check your internet connection and try again.");
      } else {
        setError(errorMessage || "Authentication failed. Please try again.");
      }
    }
  };

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    processAuth();
  }, [navigate, setUser]);

  if (error) {
    const handleClearAndRetry = () => {
      // Clear all storage
      sessionStorage.clear();
      localStorage.clear();
      // Clear cookies by redirecting to logout then back
      window.location.href = '/';
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-8">
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-serif mb-2">Access Denied</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="space-y-3">
              <Button onClick={handleClearAndRetry} className="w-full">
                Clear Cache & Return Home
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  hasProcessed.current = false;
                  setError(null);
                  setRetrying(false);
                  processAuth();
                }} 
                className="w-full"
              >
                Try Again
              </Button>
              <p className="text-xs text-muted-foreground">
                If you keep seeing this error, try clearing your browser cookies/data for this site.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">
          {retrying ? "Retrying connection..." : "Signing you in..."}
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
